// src/components/assessment/AssessmentComplete.jsx
//
// "So what → now what" in one screen, kept deliberately light: the dashboard
// already carries the full results map, so this is a one-line plain read of
// where the user landed plus the SAME next step the Guided Path will
// recommend (shared builder in src/utils/guidedPath.js). If the answers
// raised a safety flag, talking to someone is offered before the plan.

import React from 'react';
import { CheckCircle2, ChevronRight, MessageSquare, Sparkles, LayoutDashboard, HeartHandshake, Users } from 'lucide-react';
import { buildPathNodes, normalizePathProgress, pickRecommendedNode, setActivePathNode } from '../../utils/guidedPath';
import { getActionPlanForNode } from '../../data/actionPlans';
import SupportOutlet from '../dashboard/SupportOutlet';
import { useOrganization } from '../../context/OrganizationContext';
import { customSections } from '../../utils/customNav';
import { resolveAgeTier } from '../../utils/ageTier';
import { CONTEXT_SIGNALS } from '../../shared/scoringCore';
import { YOUTH_PLAIN_READ, youthQuadrant } from '../../data/youthCopy';

// Plain-language read of the Performance x Wellbeing quadrant
const PLAIN_READ = {
    thriving: 'Performance and wellbeing are both in a good place. The aim now is to keep it there.',
    building: "Wellbeing is solid and there's room to build performance — a good position to work from.",
    running_hot: 'Output is high but wellbeing is under strain. Recovery comes first this week.',
    needs_support: 'Both performance and wellbeing are under strain right now. Support and recovery come first.',
    unknown: 'Your full results are ready on your dashboard.'
};

const AssessmentComplete = ({ onNavigate, onAskAi, onCoachReveal, targetMetric, scores, currentUser, isPulseMode, audience = null }) => {
    const hasData = !!scores?.hasData;

    // Just submitted → the weekly pulse is not due, so the milestone reads done.
    const ctx = { hasData, isPulseDue: false };
    const completedMap = normalizePathProgress(currentUser?.pathProgress);
    const nodes = hasData ? buildPathNodes({ scores, isPulseDue: false }) : [];
    const next = pickRecommendedNode(nodes, completedMap, ctx);
    const plan = getActionPlanForNode(next);

    const quadrantId = scores?.quadrant?.id || 'unknown';
    const minorTier = resolveAgeTier(currentUser);
    const isMinor = minorTier === 'kid' || minorTier === 'teen';
    const read = (isMinor ? YOUTH_PLAIN_READ : PLAIN_READ)[quadrantId] || (isMinor ? YOUTH_PLAIN_READ : PLAIN_READ).unknown;
    const flagged = !!scores?.safetyFlag;

    // A context signal answered low (shared/scoringCore.js CONTEXT_SIGNALS); today
    // that is `belonging`. It is not a score and not a flag. The player gets a calm
    // pointer to their organization's own belonging page when it has one, and to a
    // person. The page is found by id, so no organization's page is named here.
    // On a family org, under-13s are on the single-screen kids surface, which has
    // no topic pages, so they get the person only.
    const { orgData } = useOrganization();
    const belonging = scores?.context?.belonging;
    const lowBelonging = Number.isFinite(belonging) && belonging < (CONTEXT_SIGNALS.belonging.lowBelow || 50);
    const belongingPage = (lowBelonging && (resolveAgeTier(currentUser) !== 'kid' || orgData?.youthSurface === 'athlete'))
        ? (customSections(orgData).flatMap(s => s.subItems).find(t => t.id.includes('belonging')) || null)
        : null;

    // A parent's check-in is about their player: no score, no quadrant, no path.
    // The hub is the results screen.
    if (audience === 'Parent') {
        return (
            <div className="flex flex-col items-center h-full text-center animate-in zoom-in p-6 md:p-8 overflow-y-auto">
                <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4 shadow-lg shrink-0">
                    <CheckCircle2 size={28} />
                </div>
                <h1 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">Thanks. That's your read for this month.</h1>
                <p className="text-slate-600 max-w-md font-medium leading-relaxed">Your Parent Hub now shows what might be worth a conversation, and the words for it. Nothing here is a score, and nothing is shared as yours.</p>
                <button
                    onClick={() => onNavigate('dashboard')}
                    className="mt-6 px-6 py-3.5 bg-brand-primary hover:bg-brand-dark text-white rounded-xl font-bold flex items-center gap-2 transition-colors shadow-md"
                >
                    Open my Parent Hub <ChevronRight size={18} />
                </button>
            </div>
        );
    }
    const perf = Number.isFinite(scores?.perfScore) ? Math.round(scores.perfScore) : null;
    const wellbeing = Number.isFinite(scores?.wellbeingScore) ? Math.round(scores.wellbeingScore) : null;

    // Leaving this screen opens the AI Coach drawer on the destination page —
    // a fresh assessment is the natural moment to show people it exists.
    const leaveTo = (route) => {
        if (onCoachReveal) onCoachReveal();
        onNavigate(route);
    };

    const startNext = () => {
        if (!next) { leaveTo('dashboard'); return; }
        setActivePathNode(next);
        leaveTo(next.route);
    };

    const handleDebrief = () => {
        onNavigate('dashboard');
        setTimeout(() => {
            if (!onAskAi) return;
            const focus = next?.label || (targetMetric && targetMetric !== 'general' ? targetMetric.replace(/_/g, ' ') : null);
            onAskAi(focus
                ? `I just completed my assessment. My lowest area was ${focus}. Can you help me build a plan for this week?`
                : 'I just completed my assessment. Can you help me build a plan for this week?');
        }, 500);
    };

    return (
        <div className="flex flex-col items-center h-full text-center animate-in zoom-in p-6 md:p-8 overflow-y-auto">
            {/* SO WHAT — one line, no second results map */}
            <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4 shadow-lg shrink-0">
                <CheckCircle2 size={28} />
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">
                {isPulseMode ? 'Pulse logged.' : 'Baseline locked.'}
            </h1>
            <p className="text-slate-600 max-w-md font-medium leading-relaxed">{read}</p>

            {hasData && (perf !== null || wellbeing !== null) && (
                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                    {perf !== null && (
                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-black">Performance {perf}</span>
                    )}
                    {wellbeing !== null && (
                        <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-black">Wellbeing {wellbeing}</span>
                    )}
                    {scores?.quadrant?.label && quadrantId !== 'unknown' && (
                        <span className="px-3 py-1 rounded-full bg-brand-light text-brand-primary text-xs font-black">{(isMinor ? youthQuadrant(scores.quadrant) : scores.quadrant).label}</span>
                    )}
                </div>
            )}

            {/* SAFETY FIRST — offered before the plan when answers raised a flag */}
            {flagged && (
                <div className="w-full max-w-lg mt-6 bg-rose-50 border border-rose-200 rounded-2xl p-4 text-left flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                        <HeartHandshake size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-slate-900">Some of your answers suggest things are heavy right now.</p>
                        <p className="text-xs text-slate-600 font-medium mt-0.5">You don't have to work through this on your own.</p>
                    </div>
                    <SupportOutlet
                        currentUser={currentUser}
                        topicId="depression"
                        className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold text-sm flex items-center justify-center gap-2 shrink-0 transition-colors"
                    />
                </div>
            )}

            {/* BELONGING — a low answer gets a door, not a verdict */}
            {lowBelonging && (
                <div className="w-full max-w-lg mt-6 bg-brand-light/60 border border-brand-primary/20 rounded-2xl p-4 text-left">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-white text-brand-primary flex items-center justify-center shrink-0">
                            <Users size={20} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-black text-slate-900">Not feeling like you belong right now?</p>
                            <p className="text-xs text-slate-600 font-medium mt-0.5 leading-relaxed">Lots of people your age feel that at some point, and it can change. You don't have to sort it out on your own.</p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                        {belongingPage && (
                            <button
                                onClick={() => leaveTo(belongingPage.id)}
                                className="flex-1 px-4 py-2.5 bg-brand-primary hover:bg-brand-dark text-white rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                            >
                                {belongingPage.label} <ChevronRight size={16} />
                            </button>
                        )}
                        {!flagged && (
                            <SupportOutlet
                                currentUser={currentUser}
                                className="flex-1 px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                            />
                        )}
                    </div>
                </div>
            )}

            {/* NOW WHAT — the same next step the Guided Path will recommend */}
            {next ? (
                <div className="w-full max-w-lg mt-6 bg-white border border-brand-primary/30 rounded-3xl p-5 md:p-6 text-left shadow-sm">
                    <span className="inline-block text-[9px] font-black text-brand-primary uppercase tracking-widest bg-brand-light px-2 py-0.5 rounded-full mb-2">
                        Your next step
                    </span>
                    <h2 className="text-lg font-black text-slate-900 leading-tight">{next.title}</h2>
                    <p className="text-sm text-slate-600 font-medium mt-1 leading-relaxed">{plan?.context || next.desc}</p>

                    {plan?.steps?.length > 0 && (
                        <ol className="mt-4 space-y-2.5">
                            {plan.steps.map((step, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="w-6 h-6 rounded-full bg-brand-light text-brand-primary flex items-center justify-center text-[11px] font-black shrink-0 mt-0.5">{i + 1}</span>
                                    <div>
                                        <p className="text-sm font-black text-slate-800 leading-tight">{step.title}</p>
                                        <p className="text-xs text-slate-500 font-medium leading-relaxed mt-0.5">{step.desc}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}

                    <button
                        onClick={startNext}
                        className="mt-5 w-full py-3.5 bg-brand-primary hover:bg-brand-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-md"
                    >
                        {next.milestone ? 'Open' : 'Start'}: {next.title} <ChevronRight size={18} />
                    </button>
                </div>
            ) : (
                <p className="text-slate-500 mt-6 max-w-md text-sm font-medium">Your results have been saved. Your guided path is on your dashboard.</p>
            )}

            {/* SECONDARY */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full max-w-lg mt-4 pb-4">
                <button
                    onClick={() => leaveTo('dashboard')}
                    className="w-full sm:w-auto px-5 py-3 bg-white border-2 border-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                    <LayoutDashboard size={16} /> See my full results
                </button>
                {onAskAi && (
                    <button
                        onClick={handleDebrief}
                        className="w-full sm:w-auto px-5 py-3 text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
                    >
                        <MessageSquare size={16} /> Debrief with Coach <Sparkles size={14} className="text-yellow-400" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default AssessmentComplete;
